import { Children, type HTMLAttributes, type MouseEventHandler, type ReactNode } from 'react';
import IconAction from '~/components/new/IconAction';
import { CheckBoxInput } from '~/components/ui/forms/CheckBox';
import Icon, { RemixIconNames, type IconNames } from '~/components/ui/Icon';
import { SwitchInput } from '~/components/ui/Switch';
import '~/styles/components/new/menu.scss';
import type { DataTestAttributes } from '~/utils/qa';
import { classNames } from '~/utils/styles';
import Chip, { type ChipMetaProps } from './Chip';

// A row wrapper: It can receive an icon and up to two children, rendered left and right sides of the row.
export type MenuRowProps = HTMLAttributes<HTMLDivElement> & {
    className?: string;
    children?: ReactNode;
    icon?: IconNames;
    negative?: boolean;
    disabled?: boolean;
    focusable?: boolean;
    'data-test-component'?: string;
    'data-test-name'?: string;
};

function MenuRow({
    icon,
    negative,
    className = '',
    disabled = false,
    focusable = true,
    children,
    onClick,
    'data-test-component': testComponent = 'new/Menu/MenuRow',
    'data-test-name': testName = '',
    ...divProps
}: MenuRowProps) {
    const arrChildren = Children.toArray(children);
    return (
        <div
            {...divProps}
            role='button'
            tabIndex={focusable && !disabled ? 0 : -1}
            onClick={!disabled ? onClick : undefined}
            className={classNames({
                'menu-row': true,
                'menu-row-negative': Boolean(negative),
                disabled: Boolean(disabled),
                [className]: Boolean(className),
            })}
            data-test-component={testComponent}
            data-test-name={testName}>
            <div className='menu-row-left'>
                {icon && (
                    <Icon
                        icon={icon}
                        colorVar={negative ? 'action-negative-dark' : 'core-primary'}
                    />
                )}
                {arrChildren[0]}
            </div>
            {arrChildren.length > 1 && <div className='menu-row-right'>{arrChildren[1]}</div>}
        </div>
    );
}

// A helper for label element. It can receive info which is rendered as an info icon with tooltip.
export type MenuRowLabelProps = HTMLAttributes<HTMLDivElement> & {
    info?: string;
    className?: string;
    children: ReactNode;
};

function MenuRowLabel({ info, className = '', children, ...divProps }: MenuRowLabelProps) {
    return (
        <div
            {...divProps}
            className={classNames({
                'menu-row-label': true,
                [className]: Boolean(className),
            })}>
            {children}

            {info && (
                <IconAction
                    title={info}
                    icon={RemixIconNames.RiInformationLine}
                    colorVar='core-tertiary'
                />
            )}
        </div>
    );
}

// TYPOLOGIES

// Tag typology
export type MenuRowTagProps = MenuRowProps &
    ChipMetaProps & {
        label: string;
        selected?: boolean;
    };

export function MenuRowTag({
    label,
    selected = false,
    color = 'grey',
    variant = 'filled',
    icon,
    ['data-test-component']: testComponent = 'new/Menu/MenuRowTag',
    ['data-test-name']: testName,
    ...MenuRowProps
}: MenuRowTagProps) {
    return (
        <MenuRow
            data-test-component={testComponent}
            data-test-name={testName}
            {...MenuRowProps}>
            <MenuRowLabel>
                <Chip
                    className='cursor-pointer'
                    size='small'
                    color={color}
                    variant={variant}
                    icon={icon}>
                    {label}
                </Chip>
            </MenuRowLabel>
            {selected && <Icon icon={RemixIconNames.RiCheckLine} />}
        </MenuRow>
    );
}

// Checkbox typology
export type MenuRowCheckboxProps = MenuRowProps & {
    label: string;
    checked?: boolean;
    id?: string;
};

export function MenuRowCheckbox({
    label,
    disabled,
    checked = false,
    ['data-test-component']: testComponent = 'new/Menu/MenuRowCheckbox',
    ['data-test-name']: testName,
    ...MenuRowProps
}: MenuRowCheckboxProps) {
    return (
        <MenuRow
            data-test-component={testComponent}
            data-test-name={testName}
            {...MenuRowProps}
            disabled={disabled}
            focusable={false}>
            <div className='menu-row-checkbox-content'>
                <CheckBoxInput
                    readOnly
                    className='p-0 flex items-center'
                    checked={checked}
                    disabled={disabled}
                />
                <MenuRowLabel>
                    <span className='menu-row-label-text'>{label}</span>
                </MenuRowLabel>
            </div>
        </MenuRow>
    );
}

// Action typology
export type MenuRowActionProps = MenuRowProps & {
    label: string;
    info?: string;
    selected?: boolean;
};

export function MenuRowAction({
    label,
    info,
    selected,
    ['data-test-component']: testComponent = 'new/Menu/MenuRowAction',
    ['data-test-name']: testName,
    ...MenuRowProps
}: MenuRowActionProps) {
    return (
        <MenuRow
            data-test-component={testComponent}
            data-test-name={testName}
            {...MenuRowProps}>
            <MenuRowLabel info={info}>
                <span className='menu-row-label-text'>{label}</span>
            </MenuRowLabel>
            {selected && <Icon icon={RemixIconNames.RiCheckLine} />}
        </MenuRow>
    );
}

// Link typology
export type MenuRowLinkProps = Omit<MenuRowActionProps, 'selected' | 'onClick'> & {
    onClick: MouseEventHandler<HTMLDivElement>;
};

export function MenuRowLink({
    label,
    ['data-test-component']: testComponent = 'new/Menu/MenuRowLink',
    ['data-test-name']: testName,
    ...MenuRowProps
}: MenuRowLinkProps) {
    return (
        <MenuRow
            data-test-component={testComponent}
            data-test-name={testName}
            {...MenuRowProps}>
            <MenuRowLabel>
                <span className='menu-row-label-text'>{label}</span>
            </MenuRowLabel>
            <Icon
                icon={RemixIconNames.RiArrowRightSLine}
                colorVar='core-tertiary'
            />
        </MenuRow>
    );
}

// Toggle typology
export type MenuRowToggleProps = MenuRowProps & {
    label: string;
    info?: string;
    selected?: boolean;
    id?: string;
};

export function MenuRowToggle({
    label,
    info,
    disabled,
    selected = false,
    ['data-test-component']: testComponent = 'new/Menu/MenuRowToggle',
    ['data-test-name']: testName,
    ...MenuRowProps
}: MenuRowToggleProps) {
    return (
        <MenuRow
            data-test-component={testComponent}
            data-test-name={testName}
            {...MenuRowProps}
            disabled={disabled}
            focusable={false}>
            <MenuRowLabel info={info}>
                <span className='menu-row-label-text'>{label}</span>
            </MenuRowLabel>
            <SwitchInput
                checked={selected}
                disabled={disabled}
                readOnly
            />
        </MenuRow>
    );
}

// Custom typology
export type MenuRowCustomProps = MenuRowProps & {
    children?: ReactNode;
};
export function MenuRowCustom({
    children,
    ['data-test-component']: testComponent = 'new/Menu/MenuRowCustom',
    ['data-test-name']: testName,
    ...MenuRowProps
}: MenuRowCustomProps) {
    return (
        <MenuRow
            data-test-component={testComponent}
            data-test-name={testName}
            {...MenuRowProps}>
            {children}
        </MenuRow>
    );
    return <MenuRow {...MenuRowProps}>{children}</MenuRow>;
}

// A configurable version of a stand-alone menu. Note this Menu component is not used into Select component but in Dropdown.
// For convenience, it can receive children as well.
export type MenuProps = HTMLAttributes<HTMLDivElement> & {
    children?: ReactNode;
    ref?: React.RefObject<HTMLDivElement>;
} & (
        | { typology: 'action'; items?: MenuRowActionProps[] }
        | { typology: 'link'; items?: MenuRowLinkProps[] }
        | { typology: 'checkbox'; items?: MenuRowCheckboxProps[] }
        | { typology: 'tag'; items?: MenuRowTagProps[] }
        | { typology: 'toggle'; items?: MenuRowToggleProps[] }
        | { typology: 'custom'; items?: MenuRowCustomProps[] }
    ) &
    DataTestAttributes;
export function Menu({
    typology,
    items = [],
    children,
    ['data-test-component']: testComponent = 'new/Menu',
    ['data-test-name']: testName,
    ...props
}: MenuProps) {
    return (
        <div
            {...props}
            data-test-component={testComponent}
            data-test-name={testName}
            className={classNames({
                'menu-container': true,
                [props.className || '']: Boolean(props.className),
            })}>
            {items.map((item, index) => {
                switch (typology) {
                    case 'action':
                    default: {
                        const actionItem = item as MenuRowActionProps;
                        return (
                            <MenuRowAction
                                key={`${actionItem.label}-${index}`}
                                {...actionItem}
                            />
                        );
                    }
                    case 'link': {
                        const linkItem = item as MenuRowLinkProps;
                        return (
                            <MenuRowLink
                                key={`${linkItem.label}-${index}`}
                                {...linkItem}
                            />
                        );
                    }
                    case 'checkbox': {
                        const checkboxItem = item as MenuRowCheckboxProps;
                        return (
                            <MenuRowCheckbox
                                key={`${checkboxItem.label}-${index}`}
                                {...checkboxItem}
                            />
                        );
                    }
                    case 'tag': {
                        const tagItem = item as MenuRowTagProps;
                        return (
                            <MenuRowTag
                                key={`${tagItem.label}-${index}`}
                                {...tagItem}
                            />
                        );
                    }
                    case 'toggle': {
                        const toggleItem = item as MenuRowToggleProps;
                        return (
                            <MenuRowToggle
                                key={`${toggleItem.label}-${index}`}
                                {...toggleItem}
                            />
                        );
                    }
                    case 'custom': {
                        const customItem = item as MenuRowCustomProps;
                        return (
                            <MenuRowCustom
                                key={`${customItem.id ?? 'custom'}-${index}`}
                                {...customItem}
                            />
                        );
                    }
                }
            })}
            {children}
        </div>
    );
}
